import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ConduitService } from '../../../shared/services/fiber-management/conduit.service.mjs';
import { CustomerService } from '../../../shared/services/customers/customers.service.mjs';
import { FiberService } from '../../../shared/services/fiber-management/fiber.service.mjs';
import { HandholeService } from '../../../shared/services/fiber-management/handhole.service.mjs';
import { CUSTOMER_STATUS, ICustomer } from '@nova/common-interfaces';

@Component({
    selector: 'app-layers',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        GoogleMapsModule
    ],
    templateUrl: './map-layers.component.html',
    styleUrls: ['./map-layers.component.scss']
})
export class LayersComponent {
    isExpanded = false; // Default collapsed state
    @Output() layerToggled = new EventEmitter<{ key: string; enabled: boolean; layer: google.maps.Data }>();

    layers = [
        { name: 'Customers', key: 'customers', enabled: false },
        { name: 'Fiber', key: 'fiber', enabled: false },
        { name: 'Handholes', key: 'handholes', enabled: false },
        { name: 'Conduit', key: 'conduit', enabled: false }
    ];

    constructor(private conduitService: ConduitService, private customerService: CustomerService,
        private fiberService: FiberService, private handholeService: HandholeService) { }

    async toggleLayer(layer: any) {
        const customLayer = layer.enabled ? await this.addLayerData(layer.key) : null;
        this.layerToggled.emit({ key: layer.key, enabled: layer.enabled, layer: customLayer });
    }

    toggleMenu() {
        this.isExpanded = !this.isExpanded;
    }

    async addLayerData(key: string): Promise<any> {
        switch (key) {
            case 'customers':
                try {
                    const response = await this.customerService.getAll();
                    let customers = response?.data ?? [];

                    // Create a Data Layer
                    const customerLayer = new google.maps.Data();

                    // Add customers to the layer as GeoJSON
                    customers.forEach((customer: ICustomer) => {
                        const feature = customerLayer.add({
                            geometry: new google.maps.Data.Point(
                                new google.maps.LatLng(customer.latitude ?? 0, customer.longitude)
                            ),
                            properties: {
                                name: customer.name,
                                email: customer.email,
                                address: `${customer.mailing_address?.line1}, ${customer.mailing_address?.postal_code}`,
                                status: customer.status // Store status in properties
                            }
                        });

                        // Apply dynamic style per feature
                        customerLayer.overrideStyle(feature, {
                            icon: {
                                url: this.getIconByCustomerStatus(customer.status),
                                scaledSize: new google.maps.Size(24, 24)
                            }
                        });
                    });
                    return customerLayer;
                } catch (error) {
                    console.error("Error fetching customers:", error);
                    return new google.maps.Data(); // Return an empty layer on failure
                }

            case 'fiber':
                const fiberIcon = "https://cdn-icons-png.flaticon.com/512/4149/4149674.png";
                this.fiberService.getAll()
                    .then((response) => {
                        let fibers = response?.data ?? [];
                        return {
                            type: 'FeatureCollection',
                            features: [
                                {
                                    type: 'Feature',
                                    geometry: { type: 'Point', coordinates: [-77.6129148389582, 39.10874036701437] },
                                    properties: { name: 'Fiber start', icon: fiberIcon }
                                },
                                {
                                    type: 'Feature',
                                    geometry: { type: 'Point', coordinates: [-77.6229148389582, 39.11874036701437] },
                                    properties: { name: 'Fiber end', icon: fiberIcon }
                                },
                                {
                                    type: 'Feature',
                                    geometry: {
                                        type: 'LineString',
                                        coordinates: [
                                            [-77.6129148389582, 39.10874036701437],
                                            [-77.6229148389582, 39.11874036701437]
                                        ]
                                    },
                                    properties: { name: 'Fiber Line', icon: fiberIcon }
                                }
                            ]
                        };
                    })
                    .catch((error) => {
                        console.error('Error fetching conduits:', error);
                    })
                    .finally(() => {
                    });

                break;

            case 'handholes':
                const handholeIcon = "https://cdn-icons-png.flaticon.com/512/5657/5657985.png";
                this.handholeService.getAll()
                    .then((response) => {
                        let handholes = response?.data ?? [];

                        return {
                            type: 'FeatureCollection',
                            features: [
                                {
                                    type: 'Feature',
                                    geometry: { type: 'Point', coordinates: [-77.6129148389582, 39.10874036701437] },
                                    properties: { name: 'Handhole 1', icon: handholeIcon }
                                },
                                {
                                    type: 'Feature',
                                    geometry: { type: 'Point', coordinates: [-77.63420084863914, 39.10367855959844] },
                                    properties: { name: 'Handhole 2', icon: handholeIcon }
                                }
                            ]
                        };
                    })
                    .catch((error) => {
                        console.error('Error fetching handholes:', error);
                    })
                    .finally(() => {
                    });

                break;

            case 'conduit':
                const conduitIcon = "https://cdn-icons-png.flaticon.com/512/2426/2426743.png";
                this.conduitService.getAll()
                    .then((response) => {
                        let conduits = response?.data ?? [];
                        return {
                            type: 'FeatureCollection',
                            features: [
                                {
                                    type: 'Feature',
                                    geometry: { type: 'Point', coordinates: [-77.6129148389582, 39.10874036701437] },
                                    properties: { name: 'Conduit start', icon: conduitIcon }
                                },
                                {
                                    type: 'Feature',
                                    geometry: { type: 'Point', coordinates: [-77.63420084863914, 39.10367855959844] },
                                    properties: { name: 'Conduit end', icon: conduitIcon }
                                },
                                {
                                    type: 'Feature',
                                    geometry: {
                                        type: 'LineString',
                                        coordinates: [
                                            [-77.6129148389582, 39.10874036701437],
                                            [-77.63420084863914, 39.10367855959844]
                                        ]
                                    },
                                    properties: { name: 'Conduit Path', icon: conduitIcon }
                                }
                            ]
                        };
                    })
                    .catch((error) => {
                        console.error('Error fetching conduits:', error);
                    })
                    .finally(() => {
                    });
                break;
            default:
                console.warn(`Unknown layer key: ${key}`);
        }
    }

    getIconByCustomerStatus(status: string): string {
        switch (status) {
            case CUSTOMER_STATUS.ACTIVE:
                return "../../../../assets/svgs/green_house.svg";
            case CUSTOMER_STATUS.INACTIVE:
                return "../../../../assets/svgs/red_house.svg";
            default:
                return "../../../../assets/svgs/black_house.svg";
        }
    }
}
